import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, CircleAlert, Info, Loader2, Pencil } from "lucide-react";
import { Button } from "../ui/button";
import { acceptBody, batchReady, batchSelection, isInternalLink, isPendingProposal, proposalOutcome } from "../../lib/copilot";
import { filesBlockAccept } from "../../lib/copilotFiles";
import ProposalFiles from "./ProposalFiles";

// The approval card of an action proposed by the Copilote (V2): what will be created or changed — written by the server from the
// real record, never by the model —, then Accepter / Refuser (with an optional reason sent back to the assistant) / Modifier (the
// usual form, when there is one). One card per action; a deletion is red and Accepter is never its default button.
// An action that takes files shows « Joindre » (ProposalFiles): Accepter stays disabled while a required one is missing.
// A BATCH card (several records, e.g. 12 overdue tasks) lists one line per record: each can be unticked, and the one shared value (a new
// due date, a reason) can be edited before accepting; the outcome then says which lines were done and which were not.
export default function ProposalCard({ proposal, busy, onAccept, onRefuse, onEdit, onNavigate }) {
  const [refusing, setRefusing] = useState(false);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { card } = proposal;
  const [excluded, setExcluded] = useState(() => new Set());
  const [commonValue, setCommonValue] = useState(card.common?.value ?? "");
  const items = card.items;
  const toggle = (id) => setExcluded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const { total, selected } = batchSelection(card, excluded);
  const notDone = proposal.status === "accepted" ? (proposal.result?.items || []).filter((i) => !i.ok) : [];
  const spec = card.attachments;
  const pending = isPendingProposal(proposal);
  const danger = card.danger;
  const outcome = proposalOutcome(proposal);
  const target = card.target || {};

  return (
    <div className={`rounded-xl border bg-white text-sm ${danger && pending ? "border-red-200" : "border-slate-200"}`}
      data-testid="copilot-proposal" data-status={proposal.status} data-kind={card.kind}>
      <div className={`rounded-t-xl border-b px-3 py-2 ${danger ? "border-red-100 bg-red-50" : "border-slate-100 bg-slate-50"}`}>
        <div className={`text-xs font-medium ${danger ? "text-red-700" : "text-slate-500"}`}>Le Copilote propose</div>
        <div className="font-semibold text-slate-900">{card.title}</div>
        {target.label && (
          target.id && isInternalLink(target.link)
            ? <Link to={target.link} onClick={onNavigate} className="block text-xs text-jamora underline-offset-2 hover:underline">{target.label}</Link>
            : <div className="text-xs text-slate-600">{target.label}</div>
        )}
      </div>

      {card.fields?.length > 0 && (
        <dl className="space-y-1.5 px-3 py-2.5" data-testid="copilot-proposal-fields">
          {card.fields.map((row) => (
            <div key={row.label} className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-2">
              <dt className="text-xs text-slate-500">{row.label}</dt>
              <dd className="min-w-0 break-words text-slate-900">
                {row.before !== undefined ? (
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <span className="text-slate-400 line-through">{row.before}</span>
                    <ArrowRight size={12} className="shrink-0 text-slate-400" aria-label="devient" />
                    <span className="font-medium">{row.after}</span>
                  </span>
                ) : <span className="whitespace-pre-wrap">{row.after}</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {items?.length > 0 && (
        <div className="border-t border-slate-100 px-3 py-2.5" data-testid="copilot-proposal-items">
          {pending && !refusing && card.common && (
            <div className="mb-2.5">
              <label htmlFor={`common-${proposal.id}`} className="mb-1 block text-xs text-slate-500">{card.common.label}</label>
              <input id={`common-${proposal.id}`} type={card.common.type === "date" ? "date" : "text"} value={commonValue} disabled={busy}
                onChange={(e) => setCommonValue(e.target.value)} maxLength={500} data-testid="copilot-proposal-common"
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-base outline-none focus-visible:ring-2 focus-visible:ring-jamora/30 md:h-9 md:text-sm" />
            </div>
          )}
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-slate-500">
            <span data-testid="copilot-proposal-selected">{pending ? `${selected} sur ${total} cochée${total > 1 ? "s" : ""}` : `${total} ligne${total > 1 ? "s" : ""}`}</span>
            {pending && !refusing && total > 1 && (
              <button type="button" className="text-jamora underline-offset-2 hover:underline" disabled={busy} data-testid="copilot-proposal-toggle-all"
                onClick={() => setExcluded(selected === total ? new Set(items.map((i) => i.id)) : new Set())}>
                {selected === total ? "Tout décocher" : "Tout cocher"}
              </button>
            )}
          </div>
          <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-100">
            {items.map((item) => {
              const off = excluded.has(item.id);
              return (
                <li key={item.id} data-testid="copilot-proposal-item" className={off ? "opacity-50" : ""}>
                  <label className={`flex min-h-[44px] items-start gap-2.5 px-2.5 py-2 md:min-h-0 ${pending && !refusing ? "cursor-pointer" : ""}`}>
                    {pending && !refusing && (
                      <input type="checkbox" checked={!off} onChange={() => toggle(item.id)} disabled={busy} className="mt-0.5 h-4 w-4 shrink-0 accent-[#083346]"
                        aria-label={`Inclure « ${item.label} »`} />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block break-words font-medium text-slate-900">{item.label}</span>
                      {item.before !== undefined && (
                        <span className="mt-0.5 inline-flex flex-wrap items-center gap-1 text-xs">
                          <span className="text-slate-400 line-through">{item.before}</span>
                          <ArrowRight size={11} className="shrink-0 text-slate-400" aria-label="devient" />
                          <span className="font-medium text-slate-700">{item.after}</span>
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {card.consequences?.length > 0 && (
        <ul className="space-y-1 border-t border-slate-100 px-3 py-2">
          {card.consequences.map((text) => (
            <li key={text} className={`flex items-start gap-1.5 text-xs ${danger ? "text-red-700" : "text-slate-600"}`}>
              <Info size={12} className="mt-0.5 shrink-0" aria-hidden="true" />{text}
            </li>
          ))}
        </ul>
      )}

      {spec && pending && !refusing && (
        <ProposalFiles spec={spec} files={files} setFiles={setFiles} uploading={uploading} setUploading={setUploading} disabled={busy} />
      )}
      {spec && proposal.status === "accepted" && proposal.files_count > 0 && (
        <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-600" data-testid="copilot-proposal-files-done">
          {proposal.files_count} fichier(s) joint(s)
        </p>
      )}

      <div className="border-t border-slate-100 px-3 py-2.5">
        {pending && !refusing && spec?.required && files.length === 0 && (
          <p className="mb-2 text-right text-xs text-amber-700" data-testid="copilot-proposal-file-required">Joignez au moins un fichier pour pouvoir accepter.</p>
        )}
        {pending && !refusing && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit} disabled={busy} data-testid="copilot-proposal-edit">
                <Pencil size={14} className="mr-1.5" />Modifier
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setRefusing(true)} disabled={busy} data-testid="copilot-proposal-refuse">
              Refuser
            </Button>
            {/* Enter accepts an ordinary card (it has the focus when it appears); never a deletion. */}
            <Button size="sm" onClick={() => onAccept(acceptBody(card, { fileIds: spec ? files.map((f) => f.id) : undefined, excluded, commonValue }))}
              disabled={busy || filesBlockAccept(spec, files, uploading) || !batchReady(card, excluded, commonValue)}
              autoFocus={!danger && !spec?.required}
              className={danger ? "bg-red-600 hover:bg-red-700" : ""} data-testid="copilot-proposal-accept">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{danger ? card.confirm || "Supprimer" : items ? `Accepter (${selected})` : "Accepter"}
            </Button>
          </div>
        )}
        {pending && refusing && (
          <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); onRefuse(reason); }}>
            <label htmlFor={`refuse-${proposal.id}`} className="text-xs text-slate-600">Pourquoi ? (facultatif, le Copilote en tiendra compte)</label>
            <textarea id={`refuse-${proposal.id}`} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
              placeholder="Ex. : plutôt lundi" data-testid="copilot-proposal-reason"
              className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-base outline-none focus-visible:ring-2 focus-visible:ring-jamora/30 md:text-sm" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setRefusing(false)} disabled={busy}>Retour</Button>
              <Button type="submit" variant="outline" size="sm" disabled={busy} data-testid="copilot-proposal-refuse-confirm">
                {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Refuser
              </Button>
            </div>
          </form>
        )}
        {notDone.length > 0 && (
          <ul className="mb-1.5 space-y-0.5 text-xs text-amber-700" data-testid="copilot-proposal-not-done">
            {notDone.map((line) => <li key={line.id}>« {line.label} » : {line.error}</li>)}
          </ul>
        )}
        {outcome && (
          <p role="status" data-testid="copilot-proposal-outcome"
            className={`flex items-start gap-1.5 text-xs ${outcome.tone === "success" ? "text-emerald-700" : outcome.tone === "error" ? "text-red-700" : "text-slate-500"}`}>
            {outcome.tone === "success" ? <CheckCircle2 size={14} className="mt-px shrink-0" /> : outcome.tone === "error" ? <CircleAlert size={14} className="mt-px shrink-0" /> : null}
            <span>
              {outcome.text}
              {proposal.status === "accepted" && proposal.result?.link && isInternalLink(proposal.result.link) && (
                <> <Link to={proposal.result.link} onClick={onNavigate} className="font-medium text-jamora underline-offset-2 hover:underline">Ouvrir</Link></>
              )}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
